import { type IAgentHandoffNotifySettings } from 'entities/Chatbot'

export const ActionTemplateMap = {
  stopConversation: '^@stopConversation\\((.+)?\\)$',
  addTag: '^@addTag\\((.+)\\)$',
  removeTag: '^@removeTag\\((.+)\\)$',
  sendSMS: '^@sendSMS\\((.+)\\)$',
  addNote: '^@addNote\\((.+)\\)$',
  handoffToHuman: '^@handoffToHuman\\((.+)?\\)$',
}

export type IAgentActionKey = keyof typeof ActionTemplateMap

export type IAgentAction = {
  key: IAgentActionKey
  label: string
  template: string
}

export type IMessageActionData = { content: string } | { auto: true } | { token_id: string }

export type ITagActionItem = {
  name: string
  id: number
}

export const stopConversationAction: IAgentAction = {
  key: 'stopConversation',
  label: 'Stop agent',
  template: '@stopConversation()',
}

export const createAddTagAction = (data: { tags: ITagActionItem[] }): IAgentAction => ({
  key: 'addTag',
  label: 'Add tag',
  template: `@addTag(${JSON.stringify(data)})`,
})

export const createRemoveTagAction = (data: { tags: ITagActionItem[] }): IAgentAction => ({
  key: 'removeTag',
  label: 'Remove tag',
  template: `@removeTag(${JSON.stringify(data)})`,
})

export const createAddContactNoteAction = (data: IMessageActionData): IAgentAction => ({
  key: 'addNote',
  label: 'Add contact note',
  template: `@addNote(${JSON.stringify(data)})`,
})

export const createSendSMSAction = (data: IMessageActionData): IAgentAction => ({
  key: 'sendSMS',
  label: 'Send SMS',
  template: `@sendSMS(${JSON.stringify(data)})`,
})

export const createHandoffToHumanAction = (data?: IAgentHandoffNotifySettings): IAgentAction => ({
  key: 'handoffToHuman',
  label: 'Handoff to human',
  template: `@handoffToHuman(${data ? JSON.stringify(data) : ''})`,
})

export const createActionChip = (action: IAgentAction): string => {
  const rootSpan = document.createElement('span')
  const wrapSpan = document.createElement('span')

  wrapSpan.className = 'actionChip-wrap'
  wrapSpan.setAttribute('data-readonly', '')
  wrapSpan.setAttribute('data-template', action.template)
  wrapSpan.setAttribute('contenteditable', 'false')

  const contentSpan = document.createElement('span')
  contentSpan.className = `actionChip-content actionChip-${action.key}`
  contentSpan.setAttribute('data-readonly', '')
  contentSpan.setAttribute('contenteditable', 'false')

  const iconSpan = document.createElement('span')
  iconSpan.className = `actionChip-icon actionChip-icon-${action.key}`
  iconSpan.setAttribute('contenteditable', 'false')

  const labelSpan = document.createElement('span')
  labelSpan.className = 'actionChip-label'
  labelSpan.setAttribute('contenteditable', 'false')
  labelSpan.innerText = action.label

  contentSpan.appendChild(iconSpan)
  contentSpan.appendChild(labelSpan)
  wrapSpan.appendChild(contentSpan)
  rootSpan.appendChild(wrapSpan)
  return rootSpan.innerHTML
}

const decodeHtmlEntities = (str: string): string =>
  str
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')

const dynamicActions = {
  [ActionTemplateMap.addTag]: createAddTagAction,
  [ActionTemplateMap.removeTag]: createRemoveTagAction,
  [ActionTemplateMap.addNote]: createAddContactNoteAction,
  [ActionTemplateMap.sendSMS]: createSendSMSAction,
  [ActionTemplateMap.handoffToHuman]: createHandoffToHumanAction,
}

export const actionChipReplacer = (template: string) => {
  const decoded = decodeHtmlEntities(template)
  if (decoded === stopConversationAction.template) return createActionChip(stopConversationAction)

  for (const [pattern, createAction] of Object.entries(dynamicActions)) {
    const match = decoded.match(pattern)
    if (match) {
      try {
        return createActionChip(createAction(match[1] ? JSON.parse(match[1]) : undefined))
      } catch {
        return template
      }
    }
  }
  return template
}
